import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { FileDown, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import ticketsService from '@/services/tickets.service';
import notesService from '@/services/notes.service';
import externalTicketsService from '@/services/external-tickets.service';
import { cleanEmailContent } from '@/lib/ticketUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
}

type Part = 'conversation' | 'internal' | 'notes';

const PARTS: Array<{ id: Part; label: string; hint: string }> = [
  { id: 'conversation', label: 'Kundenkonversation', hint: 'Alle Nachrichten mit dem Kunden' },
  { id: 'internal', label: 'Interne Kommunikation', hint: 'Lager, Lieferant, Versand … inkl. deren Notizen' },
  { id: 'notes', label: 'Notizen', hint: 'Interne Notizen am Ticket' },
];

const esc = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const fmt = (d: unknown) => {
  const date = new Date(String(d || ''));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
};

/** Nachrichtentext druckfertig machen: Zitate kürzen, gefährliche Inhalte entfernen. */
function body(content: string, contentType?: string): string {
  const raw = String(content || '');
  if (contentType === 'text' || !/<[a-z][\s\S]*>/i.test(raw)) {
    return `<div class="text">${esc(raw).replace(/\n/g, '<br>')}</div>`;
  }
  const cleaned = cleanEmailContent(raw) || raw;
  return DOMPurify.sanitize(cleaned, {
    FORBID_TAGS: ['style', 'script', 'iframe', 'form', 'input', 'button', 'link', 'meta'],
    FORBID_ATTR: ['style', 'onerror', 'onload'],
  });
}

function author(c: any): string {
  const staff = `${c.author_first_name || ''} ${c.author_last_name || ''}`.trim();
  if (c.author_role && c.author_role !== 'customer' && staff) return `${staff} (${c.author_role === 'warehouse' ? 'Lager' : 'Agent'})`;
  return c.customer_name || staff || c.customer_email || c.author_email || 'Kunde';
}

function messages(list: any[], kind: 'msg' | 'note'): string {
  if (!list.length) return '<p class="empty">Keine Einträge.</p>';
  return list.map((m) => kind === 'note'
    ? `<div class="item note"><div class="meta"><b>${esc(m.author_name || m.author_email || 'Notiz')}</b> · ${esc(fmt(m.created_at))}</div>${body(m.content)}</div>`
    : `<div class="item${m.is_public === false ? ' internal' : ''}"><div class="meta"><b>${esc(author(m))}</b> · ${esc(fmt(m.created_at))}${m.is_public === false ? ' · intern' : ''}</div>${body(m.content, m.content_type)}</div>`
  ).join('');
}

/**
 * Ticket als PDF: baut ein druckfertiges Dokument mit den gewählten Teilen und
 * öffnet den Druckdialog des Browsers („Als PDF speichern“). Kein Server-Rendering
 * nötig, Umlaute und Bilder bleiben erhalten.
 */
export default function TicketPdfExportDialog({ isOpen, onClose, ticketId }: Props) {
  const [selected, setSelected] = useState<Record<Part, boolean>>({ conversation: true, internal: true, notes: true });
  const [busy, setBusy] = useState(false);
  const any = Object.values(selected).some(Boolean);
  const all = Object.values(selected).every(Boolean);

  const exportPdf = async () => {
    setBusy(true);
    try {
      const detail: any = await ticketsService.getTicketById(ticketId);
      const ticket = detail?.data?.ticket || {};
      const sections: string[] = [];

      if (selected.conversation) {
        sections.push(`<h2>Kundenkonversation</h2>${messages(detail?.data?.comments || [], 'msg')}`);
      }
      if (selected.internal) {
        const externals = await externalTicketsService.getByParentTicketId(ticketId).catch(() => []);
        const blocks = await Promise.all(externals.map(async (ext: any) => {
          const [d, n] = await Promise.all([
            ticketsService.getTicketById(ext.id).catch(() => null) as Promise<any>,
            notesService.getNotesByTicket(ext.id).catch(() => null) as Promise<any>,
          ]);
          const party = [ext.metadata?.external_party_name, ext.metadata?.external_party_email].filter(Boolean).join(' · ');
          const items = [
            ...(d?.data?.comments || []).map((c: any) => ({ ...c, _kind: 'msg' })),
            ...(n?.data?.notes || []).map((x: any) => ({ ...x, _kind: 'note' })),
          ].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          const html = items.length
            ? items.map((it) => messages([it], it._kind)).join('')
            : '<p class="empty">Keine Nachrichten.</p>';
          return `<h3>${esc(ext.ticket_number)} – ${esc(ext.subject)}${party ? ` <span class="sub">(${esc(party)})</span>` : ''}</h3>${html}`;
        }));
        sections.push(`<h2>Interne Kommunikation</h2>${blocks.length ? blocks.join('') : '<p class="empty">Keine interne Kommunikation.</p>'}`);
      }
      if (selected.notes) {
        const n: any = await notesService.getNotesByTicket(ticketId).catch(() => null);
        sections.push(`<h2>Notizen</h2>${messages(n?.data?.notes || [], 'note')}`);
      }

      const customer = [`${ticket.requester_first_name || ''} ${ticket.requester_last_name || ''}`.trim(), ticket.requester_email].filter(Boolean).join(' · ');
      const title = `${ticket.ticket_number || 'Ticket'} – ${ticket.subject || ''}`;
      const doc = `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${esc(title)}</title><style>
        @page { margin: 16mm 14mm; }
        body { font: 11pt/1.45 -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; color: #111; }
        header { border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 14px; }
        header h1 { font-size: 15pt; margin: 0 0 4px; }
        header .meta { color: #555; font-size: 9.5pt; }
        h2 { font-size: 12.5pt; margin: 18px 0 8px; padding-bottom: 3px; border-bottom: 1px solid #ccc; }
        h3 { font-size: 11pt; margin: 14px 0 6px; } .sub { color: #666; font-weight: normal; }
        .item { border: 1px solid #e3e3e3; border-radius: 6px; padding: 8px 10px; margin: 0 0 8px; break-inside: avoid; }
        .item.internal, .item.note { background: #fff8e6; border-color: #f0d9a0; }
        .meta { color: #555; font-size: 9pt; margin-bottom: 4px; }
        .empty { color: #777; font-style: italic; }
        img { max-width: 100%; height: auto; } table { max-width: 100%; } blockquote { color: #666; border-left: 2px solid #ccc; margin: 4px 0; padding-left: 8px; }
      </style></head><body>
        <header><h1>${esc(title)}</h1><div class="meta">${esc(customer)}${ticket.status ? ` · Status: ${esc(ticket.status)}` : ''} · erstellt ${esc(fmt(ticket.created_at))} · exportiert ${esc(fmt(new Date()))}</div></header>
        ${sections.join('')}
      </body></html>`;

      // Druck über ein unsichtbares iframe: kein Popup-Blocker, Seite bleibt offen.
      const frame = document.createElement('iframe');
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      document.body.appendChild(frame);
      const w = frame.contentWindow!;
      w.document.open();
      w.document.write(doc);
      w.document.close();
      const imgs = Array.from(w.document.images);
      await Promise.race([
        Promise.all(imgs.map((img) => (img.complete ? null : new Promise((r) => { img.onload = img.onerror = r; })))),
        new Promise((r) => setTimeout(r, 4000)),
      ]);
      w.focus();
      w.print();
      setTimeout(() => frame.remove(), 60_000);
      onClose();
    } catch (error) {
      console.error('PDF export failed', error);
      toast.error('PDF konnte nicht erstellt werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileDown className="h-5 w-5" /> Als PDF exportieren</DialogTitle>
          <DialogDescription>Wählen Sie, was ins PDF soll. Im Druckdialog „Als PDF speichern“ wählen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 font-medium">
            <Checkbox checked={all} onCheckedChange={(v) => setSelected({ conversation: !!v, internal: !!v, notes: !!v })} />
            Alles
          </label>
          {PARTS.map((p) => (
            <label key={p.id} className="flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2">
              <Checkbox className="mt-0.5" checked={selected[p.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [p.id]: !!v }))} />
              <span><span className="block text-sm font-medium">{p.label}</span><span className="block text-xs text-slate-500">{p.hint}</span></span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={exportPdf} disabled={!any || busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
            PDF erstellen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
